import { Alert } from '@chakra-ui/react'
import type { AccountLifecycleStatus } from '@/api/dto/gdpr.dto'

type AccountStatusBannerProps = {
  accountStatus?: AccountLifecycleStatus
  gracePeriodEndsAt?: string | null
}

function formatDate(dateStr?: string | null) {
  if (!dateStr) return ''

  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr

  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()

  return `${day}.${month}.${year}`
}

export function AccountStatusBanner({
  accountStatus,
  gracePeriodEndsAt,
}: AccountStatusBannerProps) {
  if (!accountStatus || accountStatus === 'ACTIVE') return null

  if (accountStatus === 'GRACE_PERIOD') {
    return (
      <Alert.Root status="warning" variant="subtle">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Ihr Vertrag ist beendet.</Alert.Title>
          <Alert.Description>
            Sie befinden sich in der 6-monatigen Übergangsfrist
            {gracePeriodEndsAt ? ` (bis ${formatDate(gracePeriodEndsAt)})` : ''}. Ihr Account ist
            im Nur-Lese-Modus – eine aktive Nutzung der Software ist nicht mehr möglich. Sie können
            Ihre Daten weiterhin über den Datenexport herunterladen.
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    )
  }

  if (accountStatus === 'DEACTIVATED') {
    return (
      <Alert.Root status="error" variant="subtle">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Ihr Account wurde deaktiviert.</Alert.Title>
          <Alert.Description>
            Eine Nutzung der Software ist nicht mehr möglich. Bei Fragen wenden Sie sich bitte an
            unseren Support.
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    )
  }

  if (accountStatus === 'EXPIRED') {
    return (
      <Alert.Root status="warning" variant="subtle">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Ihr Abonnement ist abgelaufen.</Alert.Title>
          <Alert.Description>
            Bitte reaktivieren Sie Ihr Abonnement, um die Software weiter nutzen zu können.
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    )
  }

  if (accountStatus === 'NO_SUBSCRIPTION') {
    return (
      <Alert.Root status="warning" variant="subtle">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Sie haben derzeit kein aktives Abonnement.</Alert.Title>
          <Alert.Description>
            Um die Software nutzen zu können, ist ein aktives Abonnement erforderlich.
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    )
  }

  return null
}
